import { useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import {
	Layers,
	Loader2,
	Lock,
	MessageCircle,
	MoreHorizontal,
	Pencil,
	Share2,
	Trash2,
	TriangleAlert,
} from "lucide-react";
import { type CSSProperties, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";

import { APIError } from "@/adapters/api";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ChainTimeline } from "@/features/feed/components/chain-timeline";
import { CommentsSection } from "@/features/feed/components/comments-section";
import { EditPostDialog } from "@/features/feed/components/edit-post-dialog";
import { MediaViewer } from "@/features/feed/components/media-viewer";
import { ReactionButton } from "@/features/feed/components/post-card/reaction-button.tsx";
import { useDeletePost } from "@/features/feed/hooks/use-delete-post.ts";
import { useReact } from "@/features/feed/hooks/use-react.ts";
import type { Post, PostMedia } from "@/features/feed/types";
import { avatarGradient, initialsOf } from "@/features/feed/utils/avatar.ts";
import { formatPostDate, formatPostTime } from "@/features/feed/utils/format";
import { canModeratePost, isPostEdited } from "@/features/feed/utils/social.ts";
import { WEATHER_META } from "@/features/feed/utils/weather";
import { useMe } from "@/hooks/use-me.ts";
import { usePermissions } from "@/hooks/use-permissions.ts";

const MAX_MEDIA_TILES = 4;
const MEDIA_ASPECT_MIN = 4 / 5;
const MEDIA_ASPECT_MAX = 16 / 9;

export function singleMediaAspect(media: PostMedia): number | undefined {
	if (!media.width || !media.height || media.width <= 0 || media.height <= 0) {
		return undefined;
	}
	return Math.min(
		MEDIA_ASPECT_MAX,
		Math.max(MEDIA_ASPECT_MIN, media.width / media.height),
	);
}

function isViewable(media: PostMedia) {
	return (
		media.type === "image" ||
		(media.type === "video" && media.processingStatus === "ready")
	);
}

function MediaStill({ media, count }: { media: PostMedia; count: number }) {
	if (media.type === "image") {
		return (
			<img
				src={media.url}
				alt={media.caption ?? "Mídia do relato"}
				loading="lazy"
				className="post-card__media-img"
				data-count={count}
			/>
		);
	}

	if (media.type === "video") {
		if (media.processingStatus === "failed") {
			return (
				<div
					className="post-card__media-status is-failed"
					data-count={count}
				>
					<TriangleAlert />
					<span>Falha ao processar o vídeo</span>
				</div>
			);
		}

		if (media.processingStatus === "pending") {
			return (
				<div className="post-card__media-status" data-count={count}>
					{media.thumbnailUrl && (
						<img
							src={media.thumbnailUrl}
							alt=""
							className="post-card__media-status-poster"
						/>
					)}
					<div className="post-card__media-status-overlay">
						<Loader2 className="post-card__media-spinner" />
						<span>Processando vídeo…</span>
					</div>
				</div>
			);
		}

		if (media.thumbnailUrl) {
			return (
				<img
					src={media.thumbnailUrl}
					alt={media.caption ?? "Mídia do relato"}
					loading="lazy"
					className="post-card__media-img"
					data-count={count}
				/>
			);
		}

		return (
			<div className="post-card__media-placeholder" data-count={count}>
				Vídeo
			</div>
		);
	}

	return (
		<div className="post-card__media-placeholder" data-count={count}>
			Foto 360°
		</div>
	);
}

function MediaItem({ media, count }: { media: PostMedia; count: number }) {
	if (media.type === "video" && media.processingStatus === "ready") {
		return (
			// biome-ignore lint/a11y/useMediaCaption: user-uploaded video has no caption track
			<video
				controls
				preload="metadata"
				poster={media.thumbnailUrl}
				className="post-card__media-video"
				data-count={count}
			>
				<source src={media.url} />
			</video>
		);
	}

	return <MediaStill media={media} count={count} />;
}

async function sharePost(title: string, url: string) {
	if (navigator.share) {
		try {
			await navigator.share({ title, url });
			return;
		} catch {
		}
	}
	try {
		await navigator.clipboard.writeText(url);
	} catch {
	}
}

interface PostCardProps {
	post: Post;
	onStageSelect?: (stageId: string) => void;
	defaultCommentsOpen?: boolean;
}

export function PostCard({
	post,
	onStageSelect,
	defaultCommentsOpen = false,
}: PostCardProps) {
	const { obra: obraKey, postId: routePostId } = useParams();
	const permalink = `/${obraKey}/feed/${post.id}`;
	const stage = post.stage;
	const weather = post.weather ? WEATHER_META[post.weather] : null;
	const WeatherIcon = weather?.icon;
	const authorName = post.author.removed
		? "Usuário removido"
		: (post.author.name ?? "Usuário removido");
	const count = Math.min(post.media.length, MAX_MEDIA_TILES);
	const firstMedia = post.media[0];
	const mediaAspect =
		count === 1 && firstMedia ? singleMediaAspect(firstMedia) : undefined;
	const [viewerOpen, setViewerOpen] = useState(false);
	const [viewerIndex, setViewerIndex] = useState(0);
	const [commentsOpen, setCommentsOpen] = useState(defaultCommentsOpen);
	const [editOpen, setEditOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const { has, isAdmin } = usePermissions();
	const me = useMe();
	const reaction = useReact(post.id);
	const deletion = useDeletePost(post.id);
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const moderator = canModeratePost(post, {
		userId: me.data?.user.id,
		admin: isAdmin,
		role: me.data?.account?.role?.name,
	});
	const canEdit = moderator && has("posts:update");
	const canDelete = moderator && has("posts:delete");

	function confirmDelete() {
		deletion.mutate(undefined, {
			onSuccess: () => {
				setDeleteOpen(false);
				toast.success("Relato removido!");
				if (routePostId === post.id) navigate(`/${obraKey}/feed`);
			},
			onError: (error) => {
				setDeleteOpen(false);
				if (error instanceof APIError && error.code === "not-found") {
					toast.error("Este relato não está mais disponível.");
					queryClient.invalidateQueries({ queryKey: ["posts"] });
					queryClient.invalidateQueries({ queryKey: ["post", post.id] });
					return;
				}
				toast.error("Não foi possível remover o relato. Tente novamente.");
			},
		});
	}

	const viewableMedia = useMemo(
		() => post.media.filter(isViewable),
		[post.media],
	);
	const visibleMedia = post.media.slice(0, MAX_MEDIA_TILES);
	const hiddenCount = post.media.length - visibleMedia.length;

	const overflowIndex = useMemo(() => {
		const overflowTile = post.media[MAX_MEDIA_TILES - 1];
		const direct = overflowTile
			? viewableMedia.findIndex((item) => item.id === overflowTile.id)
			: -1;
		if (direct >= 0) return direct;

		const hiddenIds = new Set(
			post.media.slice(MAX_MEDIA_TILES).map((item) => item.id),
		);
		const fallback = viewableMedia.findIndex((item) => hiddenIds.has(item.id));
		return fallback >= 0 ? fallback : 0;
	}, [post.media, viewableMedia]);

	function openViewer(index: number) {
		setViewerIndex(index < 0 ? 0 : index);
		setViewerOpen(true);
	}

	return (
		<Card className={clsx("post-card", post.internal && "is-internal")}>
			{post.internal && (
				<div className="post-card__internal-banner">
					<Lock />
					Relato interno — não visível ao cliente
				</div>
			)}

			<div className="post-card__body">
				<div className="post-card__author">
					<Avatar size="lg">
						<AvatarFallback
							className="post-card__avatar-fallback"
							style={{ background: avatarGradient(authorName) }}
						>
							{initialsOf(authorName)}
						</AvatarFallback>
					</Avatar>
					<div className="post-card__author-info">
						<div className="post-card__author-line">
							{post.author.removed ? (
								<span className="post-card__author-name is-removed">
									Usuário removido
								</span>
							) : (
								<span className="post-card__author-name">
									{post.author.name}
								</span>
							)}
							{post.author.role && !post.author.removed && (
								<Badge variant="outline" className="post-card__role-badge">
									{post.author.role}
								</Badge>
							)}
						</div>
						<div className="post-card__meta">
							<span>{formatPostTime(post.date)}</span>
							<span>·</span>
							<span>{formatPostDate(post.date)}</span>
							{weather && WeatherIcon && (
								<>
									<span>·</span>
									<span className="post-card__weather">
										<WeatherIcon
											className="feed-weather-icon"
											data-weather={post.weather}
										/>
										{weather.label}
									</span>
								</>
							)}
							{isPostEdited(post) && (
								<>
									<span>·</span>
									<span className="post-card__edited">Editado</span>
								</>
							)}
						</div>
					</div>
					{(canEdit || canDelete) && (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									variant="ghost"
									size="icon-sm"
									aria-label="Mais ações"
									className="post-card__more"
								>
									<MoreHorizontal />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								{canEdit && (
									<DropdownMenuItem onClick={() => setEditOpen(true)}>
										<Pencil />
										Editar
									</DropdownMenuItem>
								)}
								{canEdit && canDelete && <DropdownMenuSeparator />}
								{canDelete && (
									<DropdownMenuItem
										variant="destructive"
										onClick={() => setDeleteOpen(true)}
									>
										<Trash2 />
										Remover
									</DropdownMenuItem>
								)}
							</DropdownMenuContent>
						</DropdownMenu>
					)}
				</div>

				{stage && (
					<div className="post-card__badges">
						{onStageSelect ? (
							<button
								type="button"
								className="post-card__stage"
								onClick={() => onStageSelect(stage.id)}
							>
								<Layers /> {stage.name}
							</button>
						) : (
							<Link
								to={`/${obraKey}/feed?etapa=${stage.id}`}
								className="post-card__stage"
							>
								<Layers /> {stage.name}
							</Link>
						)}
					</div>
				)}

				<h3 className="post-card__title">{post.title}</h3>
				<p className="post-card__text">{post.body}</p>

				{post.media.length > 0 && (
					<div
						className="post-card__media"
						data-count={count}
						style={
							mediaAspect
								? ({ "--post-media-aspect": mediaAspect } as CSSProperties)
								: undefined
						}
					>
						{visibleMedia.map((item, position) => {
							if (hiddenCount > 0 && position === MAX_MEDIA_TILES - 1) {
								return (
									<button
										key={item.id}
										type="button"
										className="post-card__media-tile"
										aria-label={`Ver mais ${hiddenCount} mídias`}
										onClick={() => openViewer(overflowIndex)}
									>
										<MediaStill media={item} count={count} />
										<span className="post-card__media-more">
											+{hiddenCount}
										</span>
									</button>
								);
							}

							if (item.type === "image") {
								return (
									<button
										key={item.id}
										type="button"
										className="post-card__media-tile"
										aria-label={`Abrir mídia ${position + 1}`}
										onClick={() =>
											openViewer(
												viewableMedia.findIndex(
													(viewable) => viewable.id === item.id,
												),
											)
										}
									>
										<MediaStill media={item} count={count} />
									</button>
								);
							}

							return <MediaItem key={item.id} media={item} count={count} />;
						})}
					</div>
				)}

				{viewableMedia.length > 0 && (
					<MediaViewer
						media={viewableMedia}
						initialIndex={viewerIndex}
						open={viewerOpen}
						onOpenChange={setViewerOpen}
					/>
				)}

				{post.chain && post.chain.length > 1 && (
					<ChainTimeline chain={post.chain} currentId={post.id} />
				)}

				<Separator className="post-card__separator" />

				<div className="post-card__actions">
					<ReactionButton
						post={post}
						disabled={!has("posts:react") || reaction.isPending}
						onReact={(type) => reaction.mutate(type)}
					/>
					<Button
						variant="ghost"
						size="sm"
						className={clsx("post-card__action", commentsOpen && "is-open")}
						onClick={() => setCommentsOpen((open) => !open)}
					>
						<MessageCircle /> Interagir
						{post.commentCount > 0 && (
							<span className="post-card__action-count">
								{post.commentCount}
							</span>
						)}
					</Button>
					<Button
						variant="ghost"
						size="sm"
						className="post-card__share"
						onClick={() =>
							sharePost(post.title, `${window.location.origin}${permalink}`)
						}
					>
						<Share2 /> Compartilhar
					</Button>
				</div>

				{commentsOpen && <CommentsSection post={post} />}
			</div>

			{canEdit && (
				<EditPostDialog post={post} open={editOpen} onOpenChange={setEditOpen} />
			)}
			{canDelete && (
				<ConfirmDialog
					open={deleteOpen}
					onOpenChange={setDeleteOpen}
					title="Remover relato?"
					description="O relato e seus comentários deixarão de aparecer no feed. Esta ação não pode ser desfeita."
					confirmLabel="Remover"
					destructive
					isPending={deletion.isPending}
					onConfirm={confirmDelete}
				/>
			)}
		</Card>
	);
}

export function PostCardSkeleton() {
	return (
		<Card className="post-card">
			<div className="post-card__body">
				<div className="post-card__author">
					<Skeleton className="post-card__skeleton-avatar" />
					<div className="post-card__author-info">
						<Skeleton className="post-card__skeleton-name" />
						<Skeleton className="post-card__skeleton-meta" />
					</div>
				</div>
				<Skeleton className="post-card__skeleton-title" />
				<div className="post-card__skeleton-lines">
					<Skeleton className="post-card__skeleton-line" />
					<Skeleton className="post-card__skeleton-line" />
					<Skeleton className="post-card__skeleton-line post-card__skeleton-line--short" />
				</div>
				<Skeleton className="post-card__skeleton-media" />
			</div>
		</Card>
	);
}
